import { useEffect, useState } from "react";
import {
  DollarSign, TrendingUp, Receipt, CarFront, Users, HandCoins, Wallet, Percent, Layers, Building2, Clock, Download,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";
import api, { downloadFile } from "@/lib/api";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const VIEWS = [["all", "All", Layers], ["fleet", "My Fleet", CarFront], ["marketplace", "Marketplace", Building2]];

function Kpi({ icon: Icon, label, value, sub, tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50 text-blue-600", green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600", slate: "bg-slate-100 text-slate-600", violet: "bg-violet-50 text-violet-600",
  };
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="bg-white rounded-2xl shadow-card p-5">
      <span className={`grid place-items-center w-10 h-10 rounded-xl ${tones[tone]}`}><Icon size={18} /></span>
      <p className="font-heading text-2xl mt-3 font-bold text-slate-900">{value}</p>
      <p className="label-caps text-slate-400 mt-1 text-[10px]">{label}</p>
      {sub && <p className="text-slate-400 text-xs mt-0.5">{sub}</p>}
    </div>
  );
}

function Card({ title, children, action }) {
  return (
    <div className="bg-white rounded-2xl shadow-card overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-heading font-semibold text-slate-900">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

function MonthlyChart({ data, bars }) {
  if (!data || data.length === 0) return <p className="text-slate-400 p-6 text-sm" data-testid="perf-no-data">No paid bookings yet — this chart populates as reservations are paid.</p>;
  return (
    <div className="p-4" style={{ width: "100%", height: 280 }}>
      <ResponsiveContainer>
        <BarChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
          <XAxis dataKey="month" fontSize={12} stroke="#94a3b8" />
          <YAxis fontSize={12} stroke="#94a3b8" tickFormatter={(v) => `$${v >= 1000 ? `${Math.round(v / 1000)}k` : v}`} />
          <Tooltip formatter={(v) => money(v)} />
          {bars.map((b) => <Bar key={b.key} dataKey={b.key} name={b.name} stackId={b.stack} fill={b.color} radius={b.radius || [4, 4, 0, 0]} />)}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Table({ cols, rows, testid }) {
  if (!rows || rows.length === 0) return <p className="text-slate-400 p-6 text-sm">No data yet.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left label-caps text-slate-400 border-b border-slate-100 bg-slate-50">
          {cols.map((c) => <th key={c.key} className={`py-3 px-5 ${c.right ? "text-right" : ""}`}>{c.label}</th>)}
        </tr></thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} data-testid={`${testid}-row`} className="border-b border-slate-50 hover:bg-slate-50/60">
              {cols.map((c) => <td key={c.key} className={`py-3 px-5 ${c.right ? "text-right" : ""} ${c.bold ? "font-semibold text-slate-900" : "text-slate-600"}`}>{c.fmt ? c.fmt(row[c.key]) : row[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AllView({ c, f, m }) {
  const t = c.totals || {};
  const split = [
    { name: "My Fleet", value: t.fleet_net || 0, color: "#2563eb" },
    { name: "Marketplace", value: t.marketplace_earnings || 0, color: "#10b981" },
  ];
  const hasSplit = (t.fleet_net || 0) + (t.marketplace_earnings || 0) > 0;
  return (
    <div className="space-y-6" data-testid="analytics-all">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Total revenue" value={money(t.revenue)} sub="fleet net + marketplace" tone="green" />
        <Kpi icon={CarFront} label="Fleet net" value={money(t.fleet_net)} sub="your own cars" />
        <Kpi icon={Building2} label="Marketplace earnings" value={money(t.marketplace_earnings)} sub="10% guest service fee" tone="violet" />
        <Kpi icon={HandCoins} label="Host payout due" value={money(t.host_payout_due)} sub="ready to pay out" tone="amber" />
        <Kpi icon={DollarSign} label="Gross volume" value={money(t.gross)} sub="all bookings" tone="slate" />
        <Kpi icon={Receipt} label="Tax collected" value={money(t.tax)} sub="to remit to MA" tone="slate" />
        <Kpi icon={CalendarIcon} label="Bookings" value={t.bookings ?? 0} sub="paid trips" tone="slate" />
        <Kpi icon={CarFront} label="Vehicles" value={t.vehicles ?? 0} sub="fleet + marketplace" tone="slate" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2"><Card title="Business revenue by month"><MonthlyChart data={c.monthly} bars={[
          { key: "fleet_net", name: "Fleet", color: "#2563eb", stack: "a" },
          { key: "marketplace", name: "Marketplace", color: "#10b981", stack: "a", radius: [4, 4, 0, 0] },
        ]} /></Card></div>
        <Card title="Revenue mix">
          {!hasSplit ? <p className="text-slate-400 p-6 text-sm">No revenue yet.</p> : (
            <div className="p-4" style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={split} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3}>
                    {split.map((s) => <Cell key={s.name} fill={s.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => money(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-5 -mt-2">
                {split.map((s) => <span key={s.name} className="inline-flex items-center gap-1.5 text-xs text-slate-600"><span className="w-2.5 h-2.5 rounded-full" style={{ background: s.color }} />{s.name} · {money(s.value)}</span>)}
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function FleetView({ f }) {
  const t = f.totals || {};
  return (
    <div className="space-y-6" data-testid="analytics-fleet">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="Net revenue" value={money(t.net)} sub="after tax" tone="green" />
        <Kpi icon={DollarSign} label="Gross collected" value={money(t.gross)} sub="excl. deposit" />
        <Kpi icon={Receipt} label="Tax collected" value={money(t.tax)} sub="to remit to MA" tone="slate" />
        <Kpi icon={CarFront} label="Paid bookings" value={t.count ?? 0} sub={`avg ${money(t.avg_booking)} · ${t.vehicles ?? 0} cars`} tone="slate" />
      </div>
      <Card title="Fleet revenue by month"><MonthlyChart data={f.monthly} bars={[
        { key: "gross", name: "Gross", color: "#bfdbfe" }, { key: "net", name: "Net", color: "#2563eb" },
      ]} /></Card>
      <Card title="Earnings by vehicle">
        <Table testid="fleet-vehicle" rows={f.per_vehicle} cols={[
          { key: "car_name", label: "Vehicle", bold: true }, { key: "count", label: "Bookings" },
          { key: "gross", label: "Gross", fmt: money }, { key: "net", label: "Net", fmt: money, bold: true },
        ]} />
      </Card>
    </div>
  );
}

function MarketView({ m }) {
  const t = m.totals || {};
  return (
    <div className="space-y-6" data-testid="analytics-marketplace">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Percent} label={`Service fee revenue (${Math.round((t.commission_rate || 0.1) * 100)}%)`} value={money(t.commission)} sub={`avg ${money(t.avg_commission)}/trip · guest service fee`} tone="green" />
        <Kpi icon={DollarSign} label="Host gross volume" value={money(t.host_gross)} sub="rental + miles + fees" tone="slate" />
        <Kpi icon={HandCoins} label="Host payout (100%)" value={money(t.host_payout)} sub="lifetime to hosts" tone="violet" />
        <Kpi icon={Wallet} label="Ready to pay hosts" value={money(t.payable)} sub={`${money(t.paid)} paid · ${money(t.pending)} pending`} tone="amber" />
        <Kpi icon={Receipt} label="Sales tax collected" value={money(t.tax)} sub="remit to MA — kept by neither" tone="slate" />
        <Kpi icon={Users} label="Active hosts" value={t.active_hosts ?? 0} sub={`${t.host_vehicles ?? 0} vehicles`} />
        <Kpi icon={CalendarIcon} label="Marketplace trips" value={t.trips ?? 0} sub="paid host bookings" tone="slate" />
      </div>
      <Card title="Marketplace earnings by month"><MonthlyChart data={m.monthly} bars={[
        { key: "gross", name: "Host gross", color: "#e2e8f0" },
        { key: "commission", name: "Service fee", color: "#10b981", stack: "b" },
      ]} /></Card>
      <Card title="Earnings by host">
        <Table testid="mkt-host" rows={m.per_host} cols={[
          { key: "host_name", label: "Host", bold: true }, { key: "trips", label: "Trips" },
          { key: "gross", label: "Gross", fmt: money }, { key: "commission", label: "Service fee revenue", fmt: money, bold: true },
          { key: "payout", label: "Paid to host", fmt: money, right: true },
        ]} />
      </Card>
    </div>
  );
}

function CalendarIcon(props) { return <Clock {...props} />; }

export function PerformancePanel() {
  const [data, setData] = useState(null);
  const [view, setView] = useState("all");
  useEffect(() => {
    api.get("/admin/analytics").then((r) => setData(r.data)).catch(() => setData({ fleet: {}, marketplace: {}, combined: {} }));
  }, []);
  if (!data) return <p className="text-slate-400 mt-6" data-testid="performance-loading">Loading performance…</p>;

  const scope = view === "all" ? "combined" : view;
  const exp = (fmt) => downloadFile(`/admin/analytics/export?fmt=${fmt}&scope=${scope}`).catch(() => toast.error("Export failed"));

  return (
    <div className="mt-6 space-y-6" data-testid="performance-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex bg-white rounded-full p-1.5 shadow-card" data-testid="analytics-toggle">
          {VIEWS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => setView(k)} data-testid={`analytics-view-${k}`}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-colors ${view === k ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}>
              <Icon size={15} /> {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => exp("csv")} data-testid="analytics-export-csv"
            className="flex items-center gap-2 bg-white shadow-card px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"><Download size={15} /> CSV</button>
          <button onClick={() => exp("pdf")} data-testid="analytics-export-pdf"
            className="flex items-center gap-2 bg-white shadow-card px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors"><Download size={15} /> PDF</button>
        </div>
      </div>
      {view === "all" && <AllView c={data.combined || {}} f={data.fleet || {}} m={data.marketplace || {}} />}
      {view === "fleet" && <FleetView f={data.fleet || {}} />}
      {view === "marketplace" && <MarketView m={data.marketplace || {}} />}
    </div>
  );
}
